export interface ProductFeature {
  titleTR: string;
  titleEN: string;
  descriptionTR: string;
  descriptionEN: string;
  icon: string;
}

export interface ProductBenefit {
  titleTR: string;
  titleEN: string;
  descriptionTR: string;
  descriptionEN: string;
}

export interface ProductUseCase {
  titleTR: string;
  titleEN: string;
  descriptionTR: string;
  descriptionEN: string;
}

export interface CustomerJourneyStep {
  stepTR: string;
  stepEN: string;
  descriptionTR: string;
  descriptionEN: string;
}

export interface ProductPageData {
  hero: {
    titleTR: string;
    titleEN: string;
    taglineTR: string;
    taglineEN: string;
    descriptionTR: string;
    descriptionEN: string;
    heroImage: string;
  };
  features: ProductFeature[];
  benefits: ProductBenefit[];
  useCases: ProductUseCase[];
  screenshots: string[];
  customerJourney?: CustomerJourneyStep[];
}

// Mock data for Verilobi product page
export const veriLobiData: ProductPageData = {
  hero: {
    titleTR: 'Verilobi',
    titleEN: 'Verilobi',
    taglineTR: 'Şantiye Puantaj ve Maliyet Analiz Sistemi',
    taglineEN: 'Construction Site Attendance and Cost Analysis System',
    descriptionTR: 'Yüz tanıma teknolojisi ile şantiyelerdeki personel puantajlarını otomatik oluşturun, maliyet analizi yapın ve iş gücünüzü verimli yönetin.',
    descriptionEN: 'Automatically create personnel attendance records at construction sites with facial recognition technology, perform cost analysis, and manage your workforce efficiently.',
    heroImage: 'https://images.unsplash.com/photo-1622611906499-c25a07b79bde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwd29ya2VycyUyMGhhcmQlMjBoYXQlMjBiaW9tZXRyaWMlMjBmYWNpYWwlMjByZWNvZ25pdGlvbnxlbnwwfDB8fHwxNzU5MjkzNjQ3fDA&ixlib=rb-4.1.0&q=85',
  },
  features: [
    {
      titleTR: 'Yüz Tanıma Teknolojisi',
      titleEN: 'Facial Recognition Technology',
      descriptionTR: 'Yapay zeka destekli yüz tanıma ile hızlı ve güvenilir kimlik doğrulama',
      descriptionEN: 'Fast and reliable identity verification with AI-powered facial recognition',
      icon: 'face',
    },
    {
      titleTR: 'Otomatik Puantaj',
      titleEN: 'Automated Attendance',
      descriptionTR: 'Personel giriş-çıkışlarını otomatik kaydedin, manuel işlemi ortadan kaldırın',
      descriptionEN: 'Automatically record personnel entry-exit, eliminate manual processes',
      icon: 'schedule',
    },
    {
      titleTR: 'Maliyet Analizi',
      titleEN: 'Cost Analytics',
      descriptionTR: 'Çalışma saatlerine dayalı detaylı maliyet raporları ve analizler',
      descriptionEN: 'Detailed cost reports and analytics based on working hours',
      icon: 'analytics',
    },
    {
      titleTR: 'Mobil Uygulama',
      titleEN: 'Mobile Application',
      descriptionTR: 'Android uygulama ile sahada kolay yüz tanıma ve kayıt işlemleri',
      descriptionEN: 'Easy facial recognition and recording in the field with Android app',
      icon: 'phone_android',
    },
    {
      titleTR: 'Masaüstü Yönetim',
      titleEN: 'Desktop Management',
      descriptionTR: 'Kapsamlı raporlama ve yönetim için masaüstü uygulaması',
      descriptionEN: 'Desktop application for comprehensive reporting and management',
      icon: 'computer',
    },
    {
      titleTR: 'Gerçek Zamanlı Takip',
      titleEN: 'Real-time Tracking',
      descriptionTR: 'Şantiyedeki personel durumunu anlık olarak takip edin',
      descriptionEN: 'Track personnel status at the construction site in real-time',
      icon: 'update',
    },
  ],
  benefits: [
    {
      titleTR: '%90 Zaman Tasarrufu',
      titleEN: '90% Time Savings',
      descriptionTR: 'Manuel puantaj işlemlerini ortadan kaldırarak zaman kazanın',
      descriptionEN: 'Save time by eliminating manual attendance processes',
    },
    {
      titleTR: '%99.9 Doğruluk',
      titleEN: '99.9% Accuracy',
      descriptionTR: 'Yüz tanıma teknolojisi ile hatasız kayıtlar',
      descriptionEN: 'Error-free records with facial recognition technology',
    },
    {
      titleTR: 'Maliyet Kontrolü',
      titleEN: 'Cost Control',
      descriptionTR: 'İş gücü maliyetlerinizi detaylı takip edin',
      descriptionEN: 'Track your workforce costs in detail',
    },
    {
      titleTR: 'Uyumluluk',
      titleEN: 'Compliance',
      descriptionTR: 'İş kanunu ve SGK gerekliliklerine tam uyum',
      descriptionEN: 'Full compliance with labor law and social security requirements',
    },
  ],
  useCases: [
    {
      titleTR: 'İnşaat Şantiyeleri',
      titleEN: 'Construction Sites',
      descriptionTR: 'Büyük ölçekli inşaat projelerinde yüzlerce personelin puantajını kolayca yönetin',
      descriptionEN: 'Easily manage attendance of hundreds of personnel in large-scale construction projects',
    },
    {
      titleTR: 'Alt Yüklenici Yönetimi',
      titleEN: 'Subcontractor Management',
      descriptionTR: 'Farklı alt yüklenicilere ait personeli ayrı ayrı takip edin',
      descriptionEN: 'Track personnel from different subcontractors separately',
    },
    {
      titleTR: 'Proje Maliyet Analizi',
      titleEN: 'Project Cost Analysis',
      descriptionTR: 'Proje bazında iş gücü maliyetlerini analiz edin',
      descriptionEN: 'Analyze workforce costs on a project basis',
    },
  ],
  screenshots: [
    'https://images.unsplash.com/photo-1703080666768-3ea98de3a114?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHw3fHxzbWFydHBob25lJTIwbW9iaWxlJTIwYXBwJTIwaW50ZXJmYWNlJTIwYmlvbWV0cmljJTIwYXV0aGVudGljYXRpb258ZW58MHwxfHxibHVlfDE3NTkyOTM2NDd8MA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1551086002-6eebfd453843?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHw0fHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwQUklMjBtYWNoaW5lJTIwbGVhcm5pbmclMjBuZXVyYWwlMjBuZXR3b3JrJTIwdGVjaG5vbG9neXxlbnwwfDB8fGJsdWV8MTc1OTI5MDUzMnww&ixlib=rb-4.1.0&q=85',
  ],
};

// Mock data for BeachBot product page
export const beachBotData: ProductPageData = {
  hero: {
    titleTR: 'BeachBot',
    titleEN: 'BeachBot',
    taglineTR: 'Akıllı Plaj ve Beach Club Yönetim Sistemi',
    taglineEN: 'Smart Beach and Beach Club Management System',
    descriptionTR: 'Rezervasyondan siparişe, mutfak yönetiminden ödemeye kadar tüm süreçleri dijitalleştirin. Garson ihtiyacını azaltın, müşteri memnuniyetini artırın.',
    descriptionEN: 'Digitize all processes from reservation to ordering, kitchen management to payment. Reduce waiter dependency, increase customer satisfaction.',
    heroImage: 'https://images.unsplash.com/photo-1743413515530-b45d0ae079d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHw0fHxiZWFjaCUyMGJhciUyMGJlYWNoJTIwY2x1YiUyMHNlYXNpZGUlMjByZXN0YXVyYW50JTIwdW1icmVsbGFzfGVufDB8MHx8fDE3NTkyOTM2NDd8MA&ixlib=rb-4.1.0&q=85',
  },
  features: [
    {
      titleTR: 'Rezervasyon Yönetimi',
      titleEN: 'Reservation Management',
      descriptionTR: 'Rezlong entegrasyonu ile kolay rezervasyon yönetimi',
      descriptionEN: 'Easy reservation management with Rezlong integration',
      icon: 'event',
    },
    {
      titleTR: 'QR Sipariş Sistemi',
      titleEN: 'QR Ordering System',
      descriptionTR: 'Şemsiye üzerindeki QR kodlar ile temassız sipariş',
      descriptionEN: 'Contactless ordering with QR codes on umbrellas',
      icon: 'qr_code_2',
    },
    {
      titleTR: 'Self-Servis Menü',
      titleEN: 'Self-Service Menu',
      descriptionTR: 'Müşteriler kendi siparişlerini kendileri verebilir',
      descriptionEN: 'Customers can place their own orders',
      icon: 'restaurant_menu',
    },
    {
      titleTR: 'Kroki Yönetimi',
      titleEN: 'Floor Plan Management',
      descriptionTR: 'Gerçek işletme krokisi ile kolay masa yönetimi',
      descriptionEN: 'Easy table management with real venue floor plan',
      icon: 'map',
    },
    {
      titleTR: 'Mutfak Entegrasyonu',
      titleEN: 'Kitchen Integration',
      descriptionTR: 'Siparişler doğrudan mutfağa iletilir, hazır olunca bildirim gönderilir',
      descriptionEN: 'Orders sent directly to kitchen, notification when ready',
      icon: 'restaurant',
    },
    {
      titleTR: 'Çevrim içi Ödeme',
      titleEN: 'Online Payment',
      descriptionTR: 'Müşteriler hesaplarını online ödeyebilir',
      descriptionEN: 'Customers can pay their bills online',
      icon: 'payment',
    },
  ],
  benefits: [
    {
      titleTR: '%60 Personel Tasarrufu',
      titleEN: '60% Staff Savings',
      descriptionTR: 'Garson ihtiyacını azaltarak işgücü maliyetlerini düşürün',
      descriptionEN: 'Reduce labor costs by decreasing waiter dependency',
    },
    {
      titleTR: 'Daha Hızlı Servis',
      titleEN: 'Faster Service',
      descriptionTR: 'Self-servis ile sipariş sürelerini kısaltın',
      descriptionEN: 'Shorten order times with self-service',
    },
    {
      titleTR: 'Artan Ciro',
      titleEN: 'Increased Revenue',
      descriptionTR: 'Daha fazla masa devri ile cironuzu artırın',
      descriptionEN: 'Increase revenue with more table turnover',
    },
    {
      titleTR: 'Müşteri Memnuniyeti',
      titleEN: 'Customer Satisfaction',
      descriptionTR: 'Hızlı ve kolay sipariş süreci ile müşteri memnuniyetini artırın',
      descriptionEN: 'Increase customer satisfaction with fast and easy ordering process',
    },
  ],
  useCases: [
    {
      titleTR: 'Beach Bar\'lar',
      titleEN: 'Beach Bars',
      descriptionTR: 'Plaj barlarında hızlı ve verimli hizmet',
      descriptionEN: 'Fast and efficient service in beach bars',
    },
    {
      titleTR: 'Beach Club\'lar',
      titleEN: 'Beach Clubs',
      descriptionTR: 'Lüks beach club\'larda premium deneyim',
      descriptionEN: 'Premium experience in luxury beach clubs',
    },
    {
      titleTR: 'Açık Hava Restoranları',
      titleEN: 'Outdoor Restaurants',
      descriptionTR: 'Geniş alanlarda kolay yönetim',
      descriptionEN: 'Easy management in large areas',
    },
  ],
  screenshots: [
    'https://images.unsplash.com/photo-1605513524042-426bace35fc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwzfHxRUiUyMGNvZGUlMjBzbWFydHBob25lJTIwc2Nhbm5pbmclMjBtb2JpbGUlMjBjb250YWN0bGVzc3xlbnwwfDF8fHwxNzU5MjkzNjQ3fDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1680014909199-ef0adcb29901?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwzfHxiZWFjaCUyMGJhciUyMGJlYWNoJTIwY2x1YiUyMHNlYXNpZGUlMjByZXN0YXVyYW50JTIwdW1icmVsbGFzfGVufDB8MHx8fDE3NTkyOTM2NDd8MA&ixlib=rb-4.1.0&q=85',
  ],
  customerJourney: [
    {
      stepTR: 'Şemsiyelerdeki QR Kodu Okut',
      stepEN: 'Scan QR Code on Umbrella',
      descriptionTR: 'Müşteri şemsiye üzerindeki QR kodu telefonuyla okutarak başlar',
      descriptionEN: 'Customer starts by scanning QR code on umbrella with phone',
    },
    {
      stepTR: 'Hesap Açılış Talebi',
      stepEN: 'Account Opening Request',
      descriptionTR: 'Garsonlara bildirim gider, hesap açılır',
      descriptionEN: 'Notification sent to waiters, account opened',
    },
    {
      stepTR: 'Self-Servis Sipariş',
      stepEN: 'Self-Service Order',
      descriptionTR: 'Müşteri web menüden kendi siparişini verir',
      descriptionEN: 'Customer places order from web menu',
    },
    {
      stepTR: 'Sipariş Hazır Bildirimi',
      stepEN: 'Order Ready Notification',
      descriptionTR: 'Sipariş hazır olduğunda müşteriye bildirim gelir',
      descriptionEN: 'Customer receives notification when order is ready',
    },
    {
      stepTR: 'Çevrim içi Ödeme',
      stepEN: 'Online Payment',
      descriptionTR: 'Müşteri hesabını online ödeyip kalkabilir',
      descriptionEN: 'Customer can pay bill online and leave',
    },
  ],
};